import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { enumLabel, useI18n } from '../lib/i18n';
import { FormGrid, FormPage } from '../components/FormPage';
import { Field, Input, Select, useToast } from '../components/ui';

/** Route: `/budgets/new`. */
export function BudgetFormPage() {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const year = new Date().getUTCFullYear();

  const [form, setForm] = useState({
    name: '',
    scopeType: 'EXPENSE_CATEGORY',
    expenseCategoryId: '',
    periodType: 'MONTHLY',
    startDate: `${year}-01-01`,
    endDate: `${year}-12-31`,
    amount: 0,
    alertThresholdPercent: 80,
  });

  const { data: categories } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => api.get<any[]>('/expense-categories'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post('/budgets', {
        ...form,
        expenseCategoryId: form.scopeType === 'EXPENSE_CATEGORY' ? form.expenseCategoryId : undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['budgets'] });
      navigate('/budgets');
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('budgets.new')}
      backTo="/budgets"
      backLabel={t('budgets.title')}
      submitLabel={t('common.create')}
      submitting={create.isPending}
      disabled={
        !form.name || form.amount <= 0 || (form.scopeType === 'EXPENSE_CATEGORY' && !form.expenseCategoryId)
      }
      onSubmit={() => create.mutate()}
    >
      <FormGrid>
        <Field label={t('common.name')} required className="sm:col-span-2">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
        </Field>
        <Field label={t('budgets.scope')} required>
          <Select value={form.scopeType} onChange={(e) => setForm({ ...form, scopeType: e.target.value })}>
            {/* Department went on 2026-10-29: an expense records none, so it counted every expense. */}
            {['COMPANY', 'EXPENSE_CATEGORY'].map((scope) => (
              <option key={scope} value={scope}>{enumLabel(scope, locale)}</option>
            ))}
          </Select>
        </Field>
        {form.scopeType === 'EXPENSE_CATEGORY' && (
          <Field label={t('expenses.categories')} required>
            <Select
              value={form.expenseCategoryId}
              onChange={(e) => setForm({ ...form, expenseCategoryId: e.target.value })}
            >
              <option value="">—</option>
              {categories?.map((c) => (
                <option key={c.id} value={c.id}>{localized(c)}</option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Period">
          <Select value={form.periodType} onChange={(e) => setForm({ ...form, periodType: e.target.value })}>
            {['MONTHLY', 'QUARTERLY', 'YEARLY', 'CUSTOM'].map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('common.amount')} required>
          <Input
            type="number"
            step="100"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
          />
        </Field>
        <Field label={t('common.from')} required>
          <Input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
        </Field>
        <Field label={t('common.to')} required>
          <Input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
        </Field>
        <Field label={`${t('budgets.threshold')} %`}>
          <Input
            type="number"
            min={1}
            max={100}
            value={form.alertThresholdPercent}
            onChange={(e) => setForm({ ...form, alertThresholdPercent: Number(e.target.value) })}
          />
        </Field>
      </FormGrid>
    </FormPage>
  );
}
